<script setup lang="ts">
import { computed, inject, useAttrs, useId, type InputHTMLAttributes } from 'vue';
import {
  FONT_SIZE_LOOKUP,
  InputHeight,
  labelInjectionKey,
  type BaseCheckedProps,
} from './internal';
import { useInputSaveStatus } from './composables/useInputSaveStatus';
import NxSaveStateBadge from './NxSaveStateBadge.vue';

const props = defineProps<
  BaseCheckedProps & { height?: InputHeight } & /* @vue-ignore */ Omit<
      InputHTMLAttributes,
      'checked'
    >
>();

let elementId = props.id;
// Attempt to retrieve the label injection (it is expected to be missing)
const labelInjection = inject(labelInjectionKey, null);
// Only run the following block if the label injection is available and no element got associated with it yet
if (labelInjection && !labelInjection.for.value) {
  // If no element ID is given, an unique identifier is generated here and assigned to the id attribute
  elementId ??= useId();
  // The element ID is sent trough the injection to the dependency provider → the label gets associated with the switch
  labelInjection.setFor(elementId);
}

const checkedModel = defineModel<boolean>({ required: true });

defineOptions({ inheritAttrs: false });

// Save status (loading/success/error) from an ancestor NxLoadingWrapper.
const {
  visualState,
  errorMessage,
  fadeOut,
  srText,
  ariaBindings,
  disabled: statusDisabled,
} = useInputSaveStatus();
const attrs = useAttrs();
const inputDisabled = computed(() => statusDisabled.value || Boolean(attrs.disabled));
</script>
<template>
  <div
    class="switch-wrapper"
    :data-save-status="visualState"
    :style="`font-size: ${FONT_SIZE_LOOKUP[props.height ?? 's']}`"
  >
    <input
      v-bind="{ ...$attrs, ...ariaBindings }"
      :id="elementId"
      v-model="checkedModel"
      role="switch"
      type="checkbox"
      :disabled="inputDisabled"
    />
    <div aria-hidden class="switch" />
    <NxSaveStateBadge
      class="save-badge"
      :state="visualState"
      :error-message="errorMessage"
      :fade-out="fadeOut"
    />
    <span class="nx-sr-only" aria-live="polite">{{ srText }}</span>
  </div>
</template>
<style scoped>
.switch-wrapper {
  position: relative;
  display: inline-flex;
  /* Hug the switch even when a parent (flex/grid) stretches the wrapper —
     the corner badge anchors to this box, so it must match the control. */
  width: fit-content;

  input {
    font-size: calc(1em / 0.875); /* 16px; em to scale with height */
    appearance: none;
    position: absolute;
    height: 100%;
    width: 3.5em; /* 56px */
    margin: 0;
    cursor: pointer;
  }

  input:disabled {
    cursor: not-allowed;
  }

  /* Replace the browser-default focus outline with the token ring on the track. */
  input:focus-visible {
    outline: none;
  }
  input:focus-visible ~ .switch {
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 3) var(--nxt-switch-ring);
  }

  /* Disabled (incl. save-loading): recolor the track (higher specificity than
     the checked-on rule below, so it wins regardless of checked state); the
     thumb stays on --nxt-switch-thumb-bg. NxSwitch renders no label itself
     (an external label consumes labelInjection), so there is no text to mute
     here. */
  input:disabled ~ .switch {
    background-color: var(--nxt-switch-track-bg-disabled);
  }

  .switch {
    font-size: calc(1em / 0.875); /* 16px; em to scale with height */
    pointer-events: none;
    margin: var(--nxt-space-xs); /* 8px */
    width: 2.5em; /* 40px */
    height: 1.125em; /* 18px */
    background-color: var(--nxt-switch-track-bg);
    border-radius: var(--nxt-radius-xl); /* 16px */

    transition: background-color var(--nxt-motion-base);

    &:before {
      position: absolute;
      content: '';
      margin: 0.0625em; /* 1px */
      width: 1em; /* 16px */
      height: 1em; /* 16px */
      background-color: var(--nxt-switch-thumb-bg);
      border-radius: var(--nxt-radius-xl); /* 16px */

      transition: transform var(--nxt-motion-base);
    }
  }

  &:has(:checked) .switch {
    background-color: var(--nxt-switch-track-bg-on);

    &:before {
      transform: translateX(1.375em); /* 22px */
    }
  }
}

/* --- I-422 save status: corner badge ---
   The badge sits in the switch's existing 8px hit-area margin, so it overlaps
   only a sliver of the track corner and the 56×32 footprint never changes. */
.save-badge {
  position: absolute;
  top: calc(var(--nxt-space-2xs) / 2);
  right: calc(var(--nxt-space-2xs) / 2);
}
.nx-sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
